// ─────────────────────────────────────────────────────────────────────────────
// calibrationReminder.js — Pure helpers for "Calibration due" detection.
//
// Status ladder:
//   • never  — pin has never been calibrated
//   • fresh  — calibrated within the last DUE_AFTER_DAYS (30) days
//   • due    — calibrated >= 30 days ago but < STALE_AFTER_DAYS (60)
//   • stale  — calibrated more than 60 days ago
//
// `firmwareChangedSince()` flips the verdict to `due` regardless of recency
// when the device's firmware revision string is different from the one we
// stored at last calibration — a firmware update often invalidates ZF/ZA.
// ─────────────────────────────────────────────────────────────────────────────

export const DUE_AFTER_DAYS = 30;
export const STALE_AFTER_DAYS = 60;

const DAY_MS = 1000 * 60 * 60 * 24;

export function calibrationDueStatus(completedAtIso, { now = Date.now() } = {}) {
  if (!completedAtIso) {
    return { status: 'never', daysSince: null };
  }
  const ts = Date.parse(completedAtIso);
  if (!Number.isFinite(ts)) {
    return { status: 'never', daysSince: null };
  }
  const daysSince = Math.floor((now - ts) / DAY_MS);
  if (daysSince < 0) {
    return { status: 'fresh', daysSince: 0 };
  }
  if (daysSince < DUE_AFTER_DAYS) {
    return { status: 'fresh', daysSince };
  }
  if (daysSince < STALE_AFTER_DAYS) {
    return { status: 'due', daysSince };
  }
  return { status: 'stale', daysSince };
}

/** Returns true when the device firmware rev differs from what we stored. */
export function firmwareChangedSince(currentFwRev, lastFwRev) {
  if (!currentFwRev || !lastFwRev) return false;
  return String(currentFwRev).trim() !== String(lastFwRev).trim();
}

/**
 * Final verdict combining time-based ladder + firmware-change check.
 * Returns the same shape as calibrationDueStatus plus a `firmwareChanged` flag
 * and a human label for the reminder banner.
 */
export function calibrationVerdict({ completedAt, currentFwRev, lastFwRev, now = Date.now() }) {
  const base = calibrationDueStatus(completedAt, { now });
  const fwChanged = firmwareChangedSince(currentFwRev, lastFwRev);
  let status = base.status;
  if (fwChanged && status === 'fresh') status = 'due';
  return {
    status,
    daysSince: base.daysSince,
    firmwareChanged: fwChanged,
    label: LABEL[status],
    detail: fwChanged
      ? `Firmware updated since last calibration (${lastFwRev} → ${currentFwRev}).`
      : null,
  };
}

const LABEL = {
  never: 'Never calibrated',
  fresh: 'Calibration current',
  due:   'Calibration due',
  stale: 'Calibration overdue',
};

export const STATUS_TONE = {
  never: { ring: 'ring-zinc-300',    text: 'text-zinc-600 dark:text-zinc-300',     bg: 'bg-zinc-100 dark:bg-zinc-800',         dot: 'bg-zinc-400'   },
  fresh: { ring: 'ring-emerald-300', text: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50 dark:bg-emerald-900/30', dot: 'bg-emerald-500' },
  due:   { ring: 'ring-amber-300',   text: 'text-amber-700 dark:text-amber-300',     bg: 'bg-amber-50 dark:bg-amber-900/30',     dot: 'bg-amber-500'   },
  stale: { ring: 'ring-rose-300',    text: 'text-rose-700 dark:text-rose-300',       bg: 'bg-rose-50 dark:bg-rose-900/30',       dot: 'bg-rose-500'    },
};
